import Button from "components/button";
import ImageUploadPreview from "components/shared/image-upload-preview";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import { Controller, UseFormReturn } from "react-hook-form";
import FleetAccordion from "./fleet-accordion";
import { FleetFormValues } from "../useAddFleet";
import { NamespaceKeys } from "locale/types";

interface FleetInformationAccordionProps {
  isOpen: boolean;
  onToggle: () => void;
  onNext: () => void;
  form: UseFormReturn<FleetFormValues>;
  t: (key: NamespaceKeys<"fleet">, options?: Record<string, unknown>) => string;
  vehicleTypeOptions: { label: string; value: string }[];
  vehicleModelOptions: { label: string; value: string }[];
  freezerModelOptions: { label: string; value: string }[];
  vendorOptions: { label: string; value: string }[];
  driverOptions: { label: string; value: string }[];
  isUploadingPicture: boolean;
  onUploadPicture: (file: File) => void;
  onSearchDrivers?: (search: string) => Promise<{ label: string; value: string }[]>;
  onSearchVendors?: (search: string) => Promise<{ label: string; value: string }[]>;
}


const FleetInformationAccordion = ({
  isOpen,
  onToggle,
  onNext,
  form,
  t,
  vehicleTypeOptions,
  vehicleModelOptions,
  freezerModelOptions,
  vendorOptions,
  driverOptions,
  isUploadingPicture,
  onUploadPicture,
  onSearchDrivers,
  onSearchVendors,
}: FleetInformationAccordionProps) => {
  const {
    register,
    control,
    formState: { errors },
  } = form;

  return (
    <FleetAccordion
      title={t("accordion-fleet-information")}
      subtitle={t("accordion-fleet-information-subtitle")}
      iconName="truck"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-[172px_1fr] gap-6">
          <div className="flex flex-col gap-[7px]">
            <label className="flex items-center gap-1 text-sm text-text-primary">
              {t("label-fleet-photo")}
              <span className="text-error">*</span>
            </label>
            <Controller
              name="fleetPhoto"
              control={control}
              render={({ field, fieldState }) => (
                <ImageUploadPreview
                  id="fleet-photo-upload"
                  dataTestId="upload-fleet-picture"
                  value={field.value}
                  onUpload={onUploadPicture}
                  onRemove={() => field.onChange("")}
                  isUploading={isUploadingPicture}
                  dropText={t("logo-drop-text")}
                  helperText={t("logo-drop-helper")}
                  accept=".jpg,.jpeg,.png"
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-6 gap-4 gap-y-5">
            <div className="col-span-3">
              <Controller
                name="plateNumber"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label={t("label-plate-number")}
                    required
                    maxLength={15}
                    data-testid="input-plate-number"
                    placeholder={t("placeholder-plate-number")}
                    value={field.value}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase().replace(/[^A-Z0-9 -]/g, "").slice(0, 15);
                      field.onChange(val);
                    }}
                    onBlur={field.onBlur}
                    error={fieldState.error}
                  />
                )}
              />
            </div>
            <div className="col-span-3">
              <Controller
                name="hullNumber"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label={t("label-hull-number")}
                    required
                    maxLength={20}
                    data-testid="input-hull-number"
                    placeholder={t("placeholder-hull-number")}
                    value={field.value}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase().replace(/[^A-Z0-9 -]/g, "").slice(0, 20);
                      field.onChange(val);
                    }}
                    onBlur={field.onBlur}
                    error={fieldState.error}
                  />
                )}
              />
            </div>

            <div className="col-span-2">
              <Controller
                name="vehicleTypeId"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    label={t("label-vehicle-type")}
                    required
                    data-testid="select-vehicle-type"
                    options={vehicleTypeOptions}
                    value={field.value}
                    onValueChange={(val) => {
                      field.onChange(val);
                      form.setValue("vehicleModelId", "");
                    }}
                    error={fieldState.error}
                    placeholder={t("placeholder-select")}
                  />
                )}
              />
            </div>
            <div className="col-span-2">
              <Controller
                name="vehicleModelId"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    label={t("label-vehicle-model")}
                    required
                    data-testid="select-vehicle-model"
                    options={vehicleModelOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    error={fieldState.error}
                    placeholder={t("placeholder-select")}
                    disabled={!form.watch("vehicleTypeId")}
                  />
                )}
              />
            </div>
            <div className="col-span-2">
              <Controller
                name="associatedAuxiliaryId"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    label={t("label-freezer-model")}
                    required
                    data-testid="select-auxiliary"
                    options={freezerModelOptions}
                    value={field.value?.[0] || ""}
                    onValueChange={(val) => field.onChange([val])}
                    error={fieldState.error}
                    placeholder={t("placeholder-select")}
                  />
                )}
              />
            </div>

            <div className="col-span-3">
              <Controller
                name="vendorId"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    label={t("label-associated-vendor")}
                    required
                    data-testid="select-vendor-association"
                    options={vendorOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    error={fieldState.error}
                    placeholder={t("placeholder-select")}
                    searchable
                    onSearch={onSearchVendors}
                  />
                )}
              />
            </div>
            <div className="col-span-3">
              <Input
                label={t("label-status")}
                value="Available"
                disabled
              />
            </div>
            <div className="col-span-6">
              <Controller
                name="driverId"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    label={t("label-driver") || "Driver"}
                    data-testid="select-driver-association"
                    options={driverOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    error={fieldState.error}
                    placeholder={t("placeholder-select")}
                    searchable
                    onSearch={onSearchDrivers}
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button type="button" variant="outline" color="tertiary" onClick={onNext} className="min-w-[100px]">
            {t("btn-next")}
          </Button>
        </div>
      </div>
    </FleetAccordion>
  );
};

export default FleetInformationAccordion;
